// #region imports

// --- Angular
import { Injectable, OnDestroy } from '@angular/core';

// --- RxJs
import {
  BehaviorSubject,
  Observable,
  Subscription,
  subscribeOn,
  take,
} from 'rxjs';

// --- HS Code
import {
  Constants,
  ContextQuestionModel,
  QuestionGroupModel,
  QuestionModel,
  QuestionResponseModel,
  QuestionTypes,
  SurveyResponseContexts,
  SurveyResponseModel,
  SurveyResponseTypes,
  SurveyStatuses,
} from '../../../models';
import { QuestionsBaseService } from '../questions/questions-base.service';

// #endregion

@Injectable({
  providedIn: 'root',
})
export abstract class ResponseBaseService implements OnDestroy {
  private baseSubs: Subscription = Subscription.EMPTY;
  private progSubs: Subscription = Subscription.EMPTY;
  private _noExtraValueQuestions: number = 0;
  private _questionResponseGroups: QuestionGroupModel[][] = new Array<
    Array<QuestionGroupModel>
  >();
  private _questionResponseGroups$: BehaviorSubject<
    QuestionGroupModel[][] | null
  >;
  get questionResponseGroups$(): Observable<QuestionGroupModel[][] | null> {
    return this._questionResponseGroups$;
  }

  private _currentQuestionResponseGroup$: BehaviorSubject<
    QuestionGroupModel[] | null
  >;
  get currentQuestionResponseGroup$(): Observable<QuestionGroupModel[] | null> {
    return this._currentQuestionResponseGroup$;
  }

  protected _indicatorResponses$: BehaviorSubject<
    QuestionResponseModel[] | null
  >;
  get indicatorResponses$(): Observable<QuestionResponseModel[] | null> {
    return this._indicatorResponses$;
  }

  protected _groupIndex$: BehaviorSubject<number | null>;
  get groupIndex$(): Observable<number | null> {
    return this._groupIndex$;
  }

  protected _currentQuestions$: BehaviorSubject<QuestionModel[] | null>;
  get currentQuestions$(): Observable<QuestionModel[] | null> {
    return this._currentQuestions$;
  }

  protected _activeResponse$: BehaviorSubject<SurveyResponseModel | null>;
  get activeResponse$(): Observable<SurveyResponseModel | null> {
    return this._activeResponse$;
  }

  protected _activeQuestionAnswers$: BehaviorSubject<
    QuestionResponseModel[] | null
  >;
  get activeQuestionAnswers$(): Observable<QuestionResponseModel[] | null> {
    return this._activeQuestionAnswers$;
  }

  protected _surveyProgress$: BehaviorSubject<number>;
  get surveyResponseProgress$(): Observable<number> {
    return this._surveyProgress$;
  }

  protected _hasExtraValueQuestions$: BehaviorSubject<boolean>;
  get hasExtraValueQuestions$(): Observable<boolean> {
    return this._hasExtraValueQuestions$;
  }

  protected _isUpdating$: BehaviorSubject<boolean>;
  get isUpdating$(): Observable<boolean> {
    return this._isUpdating$;
  }

  constructor(private questionService: QuestionsBaseService) {
    this._activeResponse$ = new BehaviorSubject<SurveyResponseModel | null>(
      null
    );
    this._questionResponseGroups$ = new BehaviorSubject<
      QuestionGroupModel[][] | null
    >(null);
    this._currentQuestionResponseGroup$ = new BehaviorSubject<
      QuestionGroupModel[] | null
    >(null);
    this._currentQuestions$ = new BehaviorSubject<QuestionModel[] | null>(null);
    this._groupIndex$ = new BehaviorSubject<number | null>(null);
    this._indicatorResponses$ = new BehaviorSubject<
      QuestionResponseModel[] | null
    >(null);
    this._activeResponse$ = new BehaviorSubject<SurveyResponseModel | null>(
      null
    );
    this._surveyProgress$ = new BehaviorSubject<number>(0);
    this._activeQuestionAnswers$ = new BehaviorSubject<
      QuestionResponseModel[] | null
    >(null);
    this._hasExtraValueQuestions$ = new BehaviorSubject<boolean>(false);
    this._isUpdating$ = new BehaviorSubject<boolean>(false);
  }

  ngOnDestroy() {
    if (this.baseSubs) {
      this.baseSubs.unsubscribe();
      this.baseSubs = Subscription.EMPTY;
    }
    if (this.progSubs) {
      this.progSubs.unsubscribe();
      this.progSubs = Subscription.EMPTY;
    }
  }

  abstract loadResponse(surveyIdentifier: string): void;
  abstract updateStatus(model: SurveyResponseModel): Observable<SurveyResponseModel | null>;
  abstract updateComment(name: string, value: string): void;
  abstract update(model: SurveyResponseModel): Observable<boolean>;
  abstract questionAnswered(response: ContextQuestionModel): void;
  abstract onNext(
    resposne: SurveyResponseModel,
    isEffectivenessNext: boolean
  ): void;

  clear(): void {
    this._activeResponse$.next(null);
    this._questionResponseGroups$.next(null);
    this._currentQuestionResponseGroup$.next(null);
    this._currentQuestions$.next(null);
    this._groupIndex$.next(null);
    this._indicatorResponses$.next(null);
    this._surveyProgress$.next(0);
    this._activeQuestionAnswers$.next(null);
    this._hasExtraValueQuestions$.next(false);
    this._isUpdating$.next(false);

    if (this.baseSubs) {
      this.baseSubs.unsubscribe();
      this.baseSubs = Subscription.EMPTY;
    }

    if (this.progSubs) {
      this.progSubs.unsubscribe();
      this.progSubs = Subscription.EMPTY;
    }
  }

  protected updateProgress(): void {
    const response = this._activeResponse$.value;
    const questionResponses = response?.questionResponses;

    if (response) {
      let contextMultiplier = 1;
      let numberOfEffectivenessQuestions = 0;
      let effectivenessMultiplier = 0;
      if (response.responseContext === SurveyResponseContexts.participant) {
        contextMultiplier = 2;
        numberOfEffectivenessQuestions =
          Constants.NUMBER_OF_EFFECTIVENESS_QUESTIONS;
        effectivenessMultiplier = 1;
      }
      let progressAdded = 0;
      if (response.status > SurveyStatuses.commentsQuestions) {
        progressAdded = Constants.ONE_ADDED_TO_SHOW_COMMENTS_QUESTION_PROGRESS;
      }

      this.progSubs = this.questionService.questions$
        .pipe(take(1))
        .subscribe((questions) => {
          let totalQuestions =
            Constants.NUMBER_OF_INDICATOR_QUESTIONS * contextMultiplier +
            numberOfEffectivenessQuestions +
            Constants.ONE_ADDED_TO_SHOW_COMMENTS_QUESTION_PROGRESS;
          if (questions && questions.length > 0) {
            totalQuestions =
              questions.filter(
                (q) => q.questionType === QuestionTypes.indicator
              ).length *
                contextMultiplier +
              questions.filter(
                (q) => q.questionType !== QuestionTypes.indicator
              ).length *
                effectivenessMultiplier +
              Constants.ONE_ADDED_TO_SHOW_COMMENTS_QUESTION_PROGRESS;
          }

          if (questionResponses) {
            const answeredQuestions =
              questionResponses.filter(
                (qa) =>
                  typeof qa.response !== 'undefined' && qa.response !== null
              ).length + progressAdded;
            console.log('answeredQuestions', answeredQuestions);
            console.log('totalQuestions', totalQuestions);
            this._surveyProgress$.next(
              Math.round(
                (answeredQuestions * 100) /
                  (totalQuestions + this._noExtraValueQuestions)
              )
            );
          } else {
            this._surveyProgress$.next(0);
          }
        });
    }
  }

  protected initialiseQuestionResponses(
    surveyId: string,
    questionResponses: QuestionResponseModel[],
    responseContext: number
  ): void {
    this._indicatorResponses$.next([] as QuestionResponseModel[]);
    this._groupIndex$.next(0);
    this._currentQuestions$.next([] as QuestionModel[]);

    if (this.baseSubs) {
      this.baseSubs.unsubscribe();
    }
    this.baseSubs = this.questionService.questions$.subscribe((questions) => {
      if (!questions) {
        return;
      }

      this._questionResponseGroups = new Array<QuestionGroupModel[]>();
      for (
        let i = 0;
        i <
        questions.filter((q) => q.questionType === QuestionTypes.indicator)
          .length;
        i += Constants.NUMBER_OF_QUESTIONS_PER_INDICATOR_GROUP
      ) {
        this._questionResponseGroups.push(new Array<QuestionGroupModel>());

        for (
          let j = 0;
          j < Constants.NUMBER_OF_QUESTIONS_PER_INDICATOR_GROUP;
          j++
        ) {
          const questionResponseGroup = this._createResponseGroup(
            questions.filter((q) => q.questionType === QuestionTypes.indicator)[
              i + j
            ],
            questionResponses
          );

          this._questionResponseGroups[
            Math.floor(i / Constants.NUMBER_OF_QUESTIONS_PER_INDICATOR_GROUP)
          ].push(questionResponseGroup);
        }
      }

      this._questionResponseGroups[5] = new Array<QuestionGroupModel>();
      for (const question of questions.filter(
        (q) => q.questionType === QuestionTypes.effectiveness
      )) {
        const questionResponseGroup = this._createResponseGroup(
          question,
          questionResponses
        );
        this._questionResponseGroups[5].push(questionResponseGroup);
      }

      const extraValueQuestions = questions.filter(
        (q) => q.questionType === QuestionTypes.extraValue
      );
      if (extraValueQuestions.length > 0) {
        this._hasExtraValueQuestions$.next(true);
        this._noExtraValueQuestions = extraValueQuestions.length;
        this._questionResponseGroups[6] = new Array<QuestionGroupModel>();
        for (const question of questions.filter(
          (q) => q.questionType === QuestionTypes.extraValue
        )) {
          const questionResponseGroup = this._createResponseGroup(
            question,
            questionResponses
          );
          this._questionResponseGroups[6].push(questionResponseGroup);
        }
      } else {
        this._hasExtraValueQuestions$.next(false);
        this._noExtraValueQuestions = 0;
      }

      const sortedResponses = questionResponses.sort((a, b) => {
        const q1 = questions.find((q) => q.id.toString() === a.questionId);
        const q2 = questions.find((q) => q.id.toString() === b.questionId);

        if (!q1 || !q2) {
          return 0;
        }
        return (
          (q1.questionType - 1) * Constants.NUMBER_OF_INDICATOR_QUESTIONS +
          q1.index -
          ((q2.questionType - 1) * Constants.NUMBER_OF_INDICATOR_QUESTIONS +
            q2.index)
        );
      });

      const firstUnanswered = sortedResponses.find(
        (qr) => typeof qr.response === 'undefined' || qr.response === null
      );
      const unansweredQuestion = questions.find(
        (q) => firstUnanswered && q.id === firstUnanswered.questionId
      );
      let currentGroupIndex = 0;
      if (unansweredQuestion) {
        if (unansweredQuestion.questionType === QuestionTypes.effectiveness) {
          currentGroupIndex = 5;
        } else if (
          unansweredQuestion.questionType === QuestionTypes.extraValue
        ) {
          currentGroupIndex = 6;
        } else {
          currentGroupIndex = Math.floor(
            ((unansweredQuestion.questionType - 1) *
              Constants.NUMBER_OF_INDICATOR_QUESTIONS +
              (unansweredQuestion.index - 1)) /
              Constants.NUMBER_OF_QUESTIONS_PER_INDICATOR_GROUP
          );
        }
      } else {
        currentGroupIndex = this._questionResponseGroups.length - 1;
      }

      if (currentGroupIndex < 0) {
        currentGroupIndex = 0;
      } else if (currentGroupIndex >= this._questionResponseGroups.length) {
        currentGroupIndex = this._questionResponseGroups.length - 1;
      }

      if (currentGroupIndex > 4) {
        let status: SurveyStatuses;

        if (responseContext === SurveyResponseContexts.participant) {
          status =
            currentGroupIndex === 5
              ? SurveyStatuses.effectivenessQuestions
              : SurveyStatuses.extraValueQuestions;
        } else {
          // responseContext === SurveyResponseContexts.respondent (because that's the only other option)
          status = SurveyStatuses.commentsQuestions;
        }

        this._updateActiveResponse(status);
      }

      this._questionResponseGroups$.next(this._questionResponseGroups);
      this._groupIndex$.next(currentGroupIndex);
      this._currentQuestionResponseGroup$.next(
        this._questionResponseGroups[currentGroupIndex]
      );

      this.updateProgress();
    });
    this.questionService.loadQuestions(surveyId);
  }

  private _createResponseGroup(
    question: QuestionModel,
    questionResponses: QuestionResponseModel[]
  ): QuestionGroupModel {
    const groupAnswers = questionResponses.filter(
      (qr) => qr.questionId === question.id.toString()
    );
    const isAnswered = groupAnswers.every(
      (ga) =>
        typeof ga.response !== 'undefined' &&
        ga.response !== null &&
        ga.response >= 0 &&
        ga.response <= 4
    );

    const questionResponseGroup = {
      questionId: question.id,
      index: question.index,
      isAnswered,
      text: question.text,
      questions: [] as ContextQuestionModel[],
    } as QuestionGroupModel;

    for (const answer of groupAnswers.sort(
      (a, b) => a.responseType - b.responseType
    )) {
      let answerTitle: string;
      switch (answer.responseType) {
        case SurveyResponseTypes.self:
          answerTitle = 'surveyQuestionsSelfScore';
          break;
        case SurveyResponseTypes.benchmark:
          answerTitle = 'surveyQuestionsBenchmark';
          break;
        default:
          answerTitle = 'surveyQuestionsSelfScore';
          break;
      }
      questionResponseGroup.questions.push({
        surveyId: answer.surveyId,
        questionId: question.id,
        responseId: answer.responseId,
        questionResponseId: answer.id,
        responseType: answer.responseType,
        title: answerTitle,
        response: answer.response,
      } as ContextQuestionModel);
    }

    return questionResponseGroup;
  }

  private _updateActiveResponse(status: SurveyStatuses) {
    if (
      this._activeResponse$.value &&
      this._activeResponse$.value.status < status
    ) {
      const activeResponse = this._activeResponse$.value;
      activeResponse.status = status;
      this._activeResponse$.next(activeResponse);
    }
  }
}
